import { useAuthDetails } from "@/context/auth-context";
import { getFirstName } from "@/lib/string";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faUserGroup,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  useExpertsData,
  useRemovedKitExpertsData,
} from "../../data/use-experts-data";
import { useKitData } from "../../data/use-kits-data";
import {
  ExtendedMessageDto,
  useMessagesData,
} from "../../data/use-messages-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { cn } from "../../lib/utils";
import { ExpertAvatar } from "../Expert/ExpertAvatar";
import { ExpertDisplayName } from "../display/ExpertDisplayName";
import { Button } from "../ui/button";
import { Row } from "../ui/flex";
import { Separator } from "../ui/separator";
import { TabsDummyButton } from "../ui/tabs";
import { useAssistContext } from "./AssistContext";
import {
  AssistAssistantMessage,
  AssistHumanMessage,
  ThinkingIcon,
  checkIfStreaming,
} from "./AssistMessage";
import { ToolApprovalCard } from "./skill/ToolApprovalCard";
import { useMessageSets } from "./use-message-sets";

type AssistBodyContentProps = {
  variant?: "default" | "minimal";
  kitType?: string;
};

type PaginationContainerProps = {
  className?: string;
};

type ExpertGroups = Record<
  "default" | string,
  {
    expert: Expert | null;
    removed: boolean;
    messages: ExtendedMessageDto[];
    status: "writing" | "complete";
  }
>;

const PAGINATION_CONTAINER_ID = "assist-body-content-pagination-container";
const TABS_CONTAINER_ID = "assist-body-content-tabs-container";

/**
 * Usage example:
 *
 * ```tsx
 * <AssistBodyContent.TabsContainer />
 * <AssistBodyContent />
 * <AssistBodyContent.PaginationContainer />
 * ```
 */
export const AssistBodyContent: React.FC<AssistBodyContentProps> & {
  TabsContainer: React.FC<PaginationContainerProps>;
  PaginationContainer: React.FC<PaginationContainerProps>;
} = ({ variant = "default", kitType }) => {
  const { activeChatUuid, activeChat, messageStage, lastUserPrompt } =
    useAssistContext();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  useKitData(groupUuid, kitUuid);
  const { experts } = useExpertsData(groupUuid, kitUuid);
  const [expertUuid, setExpertUuid] = useState<string | null>(null);
  const { messages, pendingToolApproval, submitToolApproval } = useMessagesData(
    groupUuid,
    kitUuid,
    activeChatUuid,
  );
  const authDetails = useAuthDetails();
  const { experts: kitExperts } = useExpertsData(groupUuid, kitUuid);
  const { experts: removedExperts } = useRemovedKitExpertsData(
    groupUuid,
    kitUuid,
  );

  const groups = useMemo(() => {
    const grouped: ExpertGroups = {};

    // 1. Group messages by expert uuid
    for (const message of messages) {
      const uuid = message.chat.expertUuid ?? "default";
      grouped[uuid] ??= {
        expert: null,
        removed: false,
        messages: [],
        status: "writing",
      };
      grouped[uuid].messages.push(message);
    }

    // 2. Update groups with expert data and streaming status
    for (const [uuid, group] of Object.entries(grouped)) {
      const expert = kitExperts.find((ex) => ex.uuid === uuid);
      const removedExpert = expert
        ? undefined
        : removedExperts.find((ex) => ex.uuid === uuid);

      group.expert = expert ?? removedExpert ?? null;
      group.removed = !expert && !!removedExpert;
      group.status = checkIfStreaming(group.messages) ? "writing" : "complete";
    }

    // 3. Sort: default group first, then alphabetically by expert name
    const sorted = Object.entries(grouped).sort(([uuidA, a], [uuidB, b]) => {
      if (uuidA === "default") return -1;
      if (uuidB === "default") return 1;
      if (a.expert && b.expert)
        return a.expert.name.localeCompare(b.expert.name);
      return a.expert ? -1 : b.expert ? 1 : 0;
    });

    return Object.fromEntries(sorted) as ExpertGroups;
  }, [messages, kitExperts, removedExperts]);

  const selectedMessages = useMemo(() => {
    if (!expertUuid) {
      return "default" in groups ? groups["default"].messages : messages;
    }
    return expertUuid in groups ? groups[expertUuid].messages : [];
  }, [expertUuid, groups, messages]);

  const { set, index, count, next, previous } = useMessageSets(
    selectedMessages,
    {
      followLatest: true,
    },
  );

  useEffect(() => {
    setExpertUuid(null);
  }, [kitUuid]);

  // ============================================================
  // 1. No messages
  // ============================================================

  if (count === 0 && messageStage !== "initializing") {
    if (kitType === "interview") {
      const expert = experts.length > 0 ? experts[0] : null;
      const interviewMessage = (
        <div className="text-foreground space-y-3 text-sm">
          <p>
            Welcome to Interview Mode in Salt Dash. Thank you for taking the
            time to participate. We will spend around 15-20 minutes walking
            through how you work - your approaches, methods, and perspective.
            Expect focused questions that reveal what makes your thinking
            unique.
          </p>
          <div className="space-y-2">
            <p>
              If you have helpful examples, keep them close in case your
              interviewer asks for them. Useful uploads include:
            </p>
            <ul className="list-disc space-y-1 pl-4">
              <li>Strategy briefs or decks you have written</li>
              <li>Campaign frameworks or templates</li>
              <li>Case studies or project examples</li>
            </ul>
            <p>
              Just a few focused samples are perfect and you can add them at any
              time.
            </p>
          </div>
          <p>
            When you are ready, just introduce yourself, your name, and your
            title. Your interviewer will introduce themselves and guide the
            session from there.
          </p>
        </div>
      );

      return (
        <div className="flex flex-1 flex-col justify-end p-4">
          <div className="bg-background flex w-[90%] flex-col gap-2 rounded-lg border p-4">
            <Row justify="space-between" align="center">
              <Row className="flex items-center gap-2">
                {expert && <ExpertAvatar expert={expert} size="sm" />}
                <span className="text-sm font-bold">{expert?.name}</span>
              </Row>
            </Row>
            {interviewMessage}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col justify-end p-4">
        <div className="text-2xl">
          How can I help
          {!authDetails ? (
            <span>How can I help</span>
          ) : (
            <>
              ,{" "}
              <span className="font-bold">
                {getFirstName(authDetails.name)}
              </span>
              ?
            </>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // 2. User just sent a message, no streaming yet
  // ============================================================

  if (messageStage === "initializing") {
    return (
      <div
        className={cn("flex flex-1 flex-col gap-4", {
          "p-4": variant === "default",
        })}
      >
        <AssistHumanMessage
          message={{
            uuid: "",
            type: "user",
            content: lastUserPrompt,
            order: 0,
            isStreaming: true,
            chat: {
              uuid: "",
              expertUuid: null,
              isSummary: false,
            },
            dateCreated: new Date().toISOString(),
          }}
        />
      </div>
    );
  }

  const paginationContainer = document.getElementById(PAGINATION_CONTAINER_ID);
  const pagination =
    messages.length === 0 ? null : (
      <Row className="w-full items-center justify-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={previous}
          disabled={index === 0}
          title="Previous"
          aria-label="Previous"
        >
          <FontAwesomeIcon icon={faChevronLeft} size="sm" />
        </Button>
        <span className="text-foreground text-xs">
          {index + 1} / {count}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={next}
          disabled={index === count - 1}
          title="Next"
          aria-label="Next"
        >
          <FontAwesomeIcon icon={faChevronRight} size="sm" />
        </Button>
      </Row>
    );

  const tabsContainer = document.getElementById(TABS_CONTAINER_ID);
  const tabs =
    activeChat?.summarizedChats && activeChat.summarizedChats.length > 0 ? (
      <div className="flex w-full flex-row flex-wrap items-center gap-2 px-4">
        {Object.entries(groups).map(([groupExpertUuid, group]) => (
          <>
            <TabsDummyButton
              key={
                groupExpertUuid === "default"
                  ? "default-group"
                  : groupExpertUuid
              }
              className="flex grow-0 items-center gap-2 px-2"
              isActive={
                expertUuid === groupExpertUuid ||
                (groupExpertUuid === "default" && !expertUuid)
              }
              onClick={() =>
                setExpertUuid(
                  groupExpertUuid === "default" ? null : groupExpertUuid,
                )
              }
            >
              {group.expert ? (
                <ExpertDisplayName expert={group.expert} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserGroup} />
                  Summary
                </>
              )}
              <ThinkingIcon loading={group.status === "writing"} />
            </TabsDummyButton>
            {groupExpertUuid === "default" && (
              <Separator orientation="vertical" className="h-6" />
            )}
          </>
        ))}
      </div>
    ) : null;

  // ============================================================
  // 3. Streaming or messages
  // ============================================================

  return (
    <>
      {tabsContainer ? createPortal(tabs, tabsContainer) : tabs}
      <div
        className={cn("flex flex-1 flex-col gap-4", {
          "p-4": variant === "default",
        })}
      >
        {set.userMessage && (
          <AssistHumanMessage
            message={set.userMessage}
            key={set.userMessage.uuid}
            showDelete={index > 0}
          />
        )}
        {set.responseMessages.length > 0 && (
          <AssistAssistantMessage
            key={`responses-${set.userMessage?.uuid ?? "initial"}`}
            isLastMessageInMainThread={
              (experts.length === 1 || expertUuid === null) &&
              index === count - 1
            }
            messages={set.responseMessages}
          />
        )}
        {pendingToolApproval && (
          <ToolApprovalCard
            approval={pendingToolApproval}
            onSubmit={(params) => submitToolApproval(params)}
          />
        )}
      </div>
      {paginationContainer
        ? createPortal(pagination, paginationContainer)
        : pagination}
    </>
  );
};

AssistBodyContent.PaginationContainer = ({
  className,
}: PaginationContainerProps) => {
  return <div className={className} id={PAGINATION_CONTAINER_ID} />;
};

AssistBodyContent.TabsContainer = ({ className }: PaginationContainerProps) => {
  return <div className={className} id={TABS_CONTAINER_ID} />;
};
